import './App.css';
import {
    Button,
    Card,
    Col,
    Divider,
    Flex,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Skeleton,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import useSWR, { useSWRConfig } from 'swr';
import 'react-quill/dist/quill.snow.css';
import cookie from 'react-cookies';

import { useNavigate } from 'react-router-dom';
import { LeftCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { SetStateAction, useEffect, useState } from 'react';
import type { TableProps } from 'antd';
import Loading from './Loading';

const { Title, Text } = Typography;

interface DataType {
    id: number;
    key: string;
    name: string;
    artist: string;
    categories: string;
}

const fetcher = async (url: string) => {
    const token = cookie.load('token');
    const res = await fetch(url, {
        headers: new Headers({
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${token}`,
        }),
    });
    const json = await res.json();
    if (!json.success) {
        const error = new Error((await json).message);
        throw error;
    }
    return json;
};

const SongManage = () => {
    const { mutate } = useSWRConfig();
    const [form] = Form.useForm();
    const [addSongForm] = Form.useForm();
    const [editSongForm] = Form.useForm();
    const [renameForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [addSongOpen, setAddSongOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteSongOpen, setDeleteSongOpen] = useState(false);
    const [editSongOpen, setEditSongOpen] = useState(false);
    const [editSong, setEditSong] = useState<any>({});
    const [deleteId, setDeleteId] = useState(0);
    const [deleteSongId, setDeleteSongId] = useState(0);
    const [renameId, setRenameId] = useState(0);
    const token = cookie.load('token');
    const onCreate = async (values: any) => {
        setOpen(false);
        const apiResponse = await (
            await fetch('https://api.lynne.com.cn/songListManage/addCategory', {
                method: 'POST',
                body: JSON.stringify({
                    name: values.name,
                }),
                headers: new Headers({
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${token}`,
                    'Content-type': 'Application/json',
                }),
            })
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const onCreateSong = async (values: any) => {
        setAddSongOpen(false);
        const apiResponse = await (
            await fetch('https://api.lynne.com.cn/songListManage/addSong', {
                method: 'POST',
                body: JSON.stringify(values),
                headers: new Headers({
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${token}`,
                    'Content-type': 'Application/json',
                }),
            })
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const onEditSong = async (values: any) => {
        setEditSongOpen(false);
        const apiResponse = await (
            await fetch('https://api.lynne.com.cn/songListManage/editSong', {
                method: 'POST',
                body: JSON.stringify({ id: editSong.id, ...values }),
                headers: new Headers({
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${token}`,
                    'Content-type': 'Application/json',
                }),
            })
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const onRename = async (values: any) => {
        setRenameOpen(false);
        const apiResponse = await (
            await fetch(
                'https://api.lynne.com.cn/songListManage/renameCategory',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        id: renameId,
                        name: values.name,
                    }),
                    headers: new Headers({
                        'Access-Control-Allow-Origin': '*',
                        Authorization: `Bearer ${token}`,
                        'Content-type': 'Application/json',
                    }),
                }
            )
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const onDelete = async () => {
        setDeleteOpen(false);
        const apiResponse = await (
            await fetch(
                'https://api.lynne.com.cn/songListManage/deleteCategory',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        id: deleteId,
                    }),
                    headers: new Headers({
                        'Access-Control-Allow-Origin': '*',
                        Authorization: `Bearer ${token}`,
                        'Content-type': 'Application/json',
                    }),
                }
            )
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const onDeleteSong = async () => {
        setDeleteSongOpen(false);
        const apiResponse = await (
            await fetch('https://api.lynne.com.cn/songListManage/deleteSong', {
                method: 'POST',
                body: JSON.stringify({
                    id: deleteSongId,
                }),
                headers: new Headers({
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${token}`,
                    'Content-type': 'Application/json',
                }),
            })
        ).json();
        if (apiResponse.success) {
            message.success(apiResponse.message);
            mutate('https://api.lynne.com.cn/songList/list');
        } else {
            message.error(apiResponse.message);
        }
    };

    const columns: TableProps<DataType>['columns'] = [
        {
            title: '歌名',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '歌手',
            dataIndex: 'artist',
            key: 'artist',
        },
        {
            title: '分类',
            key: 'categories',
            dataIndex: 'categories',
            render: (_, { categories }) => (
                <>
                    {categories.split(',').map(tag => {
                        return (
                            <Tag color='blue' key={tag}>
                                {tag.toUpperCase()}
                            </Tag>
                        );
                    })}
                </>
            ),
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Flex align='middle' gap={10} wrap>
                    <Button
                        type='primary'
                        onClick={() => {
                            setEditSong(record);
                            setEditSongOpen(true);
                        }}>
                        编辑
                    </Button>
                    <Button
                        type='primary'
                        danger
                        onClick={() => {
                            setDeleteSongId(record.id);
                            setDeleteSongOpen(true);
                        }}>
                        删除
                    </Button>
                </Flex>
            ),
        },
    ];

    const {
        data: songData,
        error: songError,
        isLoading: songIsLoading,
    } = useSWR('https://api.lynne.com.cn/songList/list', fetcher);
    const n = useNavigate();
    
        const [filterData, setFilterData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [searchCategory, setSearchCategory] = useState([]);
    const handleSearch = (e: { target: { value: SetStateAction<string> } }) => {
        setSearchText(e.target.value);
    };
    const handleCategory = (value: any, option: any) => {
        setSearchCategory(value);
    };
    useEffect(() => {
        if (songData && songData.data) {
            setFilterData(
                songData.data.filter((item: any) => {
                    if (searchText !== '') {
                        if (
                            item.name.indexOf(searchText) === -1 &&
                            item.artist.indexOf(searchText) === -1
                        ) {
                            return false;
                        }
                    }
                    if (searchCategory.length !== 0) {
                        let contain = false;
                        searchCategory.forEach(category => {
                            if (
                                item.category_id
                                    .split(',')
                                    .map((i: string) => parseInt(i))
                                    .includes(category)
                            ) {
                                contain = true;
                            }
                        });
                        if (!contain) {
                            return false;
                        }
                    }
                    return true;
                })
            );
        }
    }, [songData, searchCategory, searchText]);
    const getArtistInfo = async (e:any) => {
        message.info("正在匹配歌手中~");
        const songName = e.target.value;
        if (songName === '') {
            return;
        }
        const result =await (await fetch('https://bemfa.kanosaikou.cn/song?name='+songName)).json();
        const songInfo = result.data.data.song.list;
        if (songInfo.length === 0) {
            return;
        }
        const artist = songInfo[0].singer.map((s: { name: any; }) => s.name).join("/");
        addSongForm.setFieldValue('artist', artist);
        message.success(`从QQ音乐的第一个搜索结果自动匹配到了歌手${artist}喔~`);
    }
    
    if (songIsLoading || songData === undefined) return <Loading />;
    if (songError) {
        cookie.remove('token');
        n('/login');
    }

    // 渲染数据
    return (
        <>
            <Modal
                open={open}
                title='添加分类'
                okText='添加'
                cancelText='取消'
                okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
                onCancel={() => setOpen(false)}
                destroyOnClose
                modalRender={dom => (
                    <Form
                        layout='vertical'
                        form={form}
                        name='addCategory'
                        clearOnDestroy
                        onFinish={values => onCreate(values)}>
                        {dom}
                    </Form>
                )}>
                <Form.Item
                    name='name'
                    label='名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入分类名称',
                        },
                    ]}>
                    <Input />
                </Form.Item>
            </Modal>
            <Modal
                open={addSongOpen}
                title='添加歌曲'
                okText='添加'
                cancelText='取消'
                okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
                onCancel={() => setAddSongOpen(false)}
                destroyOnClose
                maskClosable={false}
                modalRender={dom => (
                    <Form
                        layout='vertical'
                        form={addSongForm}
                        name='addSong'
                        clearOnDestroy
                        onFinish={values => onCreateSong(values)}>
                        {dom}
                    </Form>
                )}>
                <Form.Item
                    name='name'
                    label='名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入歌曲名称',
                        },
                    ]}>
                    <Input autoComplete='off' onBlur={getArtistInfo} />
                </Form.Item>
                <Form.Item
                    name='artist'
                    label='歌手名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入歌手名称',
                        },
                    ]}>
                    <Input />
                </Form.Item>
                <Form.Item
                    name='categories'
                    label='类别'
                    rules={[
                        {
                            required: true,
                            message: '请选择分类',
                        },
                    ]}>
                    <Select
                        mode='multiple'
                        allowClear
                        style={{ width: '100%' }}
                        options={songData.categories.map(
                            (category: { id: number; name: string }) => {
                                return {
                                    value: category.id,
                                    label: category.name,
                                };
                            }
                        )}
                    />
                </Form.Item>
            </Modal>
            <Modal
                open={editSongOpen}
                title='编辑歌曲'
                okText='编辑'
                cancelText='取消'
                okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
                onCancel={() => setEditSongOpen(false)}
                destroyOnClose
                maskClosable={false}
                modalRender={dom => {
                    const initial = { ...editSong };
                    initial.categories = initial.category_id
                        .split(',')
                        .map((i: string) => parseInt(i));
                    editSongForm.setFieldsValue(initial);
                    return (
                        <Form
                            layout='vertical'
                            form={editSongForm}
                            name='editSong'
                            clearOnDestroy
                            onFinish={values => onEditSong(values)}>
                            {dom}
                        </Form>
                    );
                }}>
                <Form.Item
                    name='name'
                    label='名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入歌曲名称',
                        },
                    ]}>
                    <Input autoComplete='off' />
                </Form.Item>
                <Form.Item
                    name='artist'
                    label='歌手名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入歌手名称',
                        },
                    ]}>
                    <Input autoComplete='off' />
                </Form.Item>
                <Form.Item
                    name='categories'
                    label='类别'
                    rules={[
                        {
                            required: true,
                            message: '请选择分类',
                        },
                    ]}>
                    <Select
                        mode='multiple'
                        allowClear
                        style={{ width: '100%' }}
                        options={songData.categories.map(
                            (category: { id: number; name: string }) => {
                                return {
                                    value: category.id,
                                    label: category.name,
                                };
                            }
                        )}
                    />
                </Form.Item>
            </Modal>
            <Modal
                open={renameOpen}
                title='重命名分类'
                okText='编辑'
                cancelText='取消'
                okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
                onCancel={() => setRenameOpen(false)}
                destroyOnClose
                modalRender={dom => (
                    <Form
                        layout='vertical'
                        form={renameForm}
                        name='renameCategory'
                        clearOnDestroy
                        onFinish={values => onRename(values)}>
                        {dom}
                    </Form>
                )}>
                <Form.Item
                    name='name'
                    label='名称'
                    rules={[
                        {
                            required: true,
                            message: '请输入新的分类名称',
                        },
                    ]}>
                    <Input />
                </Form.Item>
            </Modal>
            <Modal
                open={deleteOpen}
                title='确认删除喵？'
                okText='删除'
                okType='danger'
                cancelText='取消'
                onCancel={() => setDeleteOpen(false)}
                onOk={() => onDelete()}
                destroyOnClose>
                这个分类会被永远删除喵！（如果分类下还有歌曲会删除失败~防止误删）
            </Modal>
            <Modal
                open={deleteSongOpen}
                title='确认删除喵？'
                okText='删除'
                okType='danger'
                cancelText='取消'
                onCancel={() => setDeleteSongOpen(false)}
                onOk={() => onDeleteSong()}
                destroyOnClose>
                这个歌曲会被永远删除喵！
            </Modal>
            <Flex align='center' gap={10}>
                <Button
                    type='primary'
                    shape='circle'
                    icon={<LeftCircleOutlined />}
                    onClick={() => n('/dashboard')}
                />
                <Title level={3} style={{ margin: '15px 0', flex: 1 }}>
                    歌单管理
                </Title>
            </Flex>
            <Flex align='center' gap={10}>
                <Button type='primary' onClick={() => setOpen(true)}>
                    添加分类
                </Button>
                <Button type='primary' onClick={() => setAddSongOpen(true)}>
                    添加歌曲
                </Button>
            </Flex>
            <Divider />
            <Space
                direction='vertical'
                size={'middle'}
                style={{ width: '100%' }}>
                {songData.categories.map(
                    (category: { id: number; name: string }) => {
                        return (
                            <div key={category.id}>
                                <Card
                                    bordered={false}
                                    style={{ width: '100%' }}>
                                    <Flex align='middle' gap={10} wrap>
                                        <Space style={{ flex: 1 }}>
                                            {category.name}
                                        </Space>
                                        <Button
                                            type='primary'
                                            onClick={() => {
                                                setRenameOpen(true);
                                                setRenameId(category.id);
                                            }}>
                                            重命名分类
                                        </Button>
                                        <Button
                                            type='primary'
                                            danger
                                            onClick={() => {
                                                setDeleteId(category.id);
                                                setDeleteOpen(true);
                                            }}>
                                            删除分类
                                        </Button>
                                    </Flex>
                                </Card>
                            </div>
                        );
                    }
                )}
                {songData.categories.length === 0 && (
                    <Card bordered={false} style={{ width: '100%' }}>
                        还没有分类喔
                    </Card>
                )}
            </Space>
            <Divider />
            <Space
                direction='vertical'
                style={{ margin: '20px 0', width: '100%' }}>
                <Row gutter={[12, 12]}>
                    <Col flex={'1 1'}>
                        <Input
                            placeholder='搜索歌曲'
                            onChange={handleSearch}
                            prefix={<SearchOutlined />}
                        />
                    </Col>
                    <Col flex={'1 1'}>
                        <Select
                            mode='multiple'
                            placeholder='类别筛选'
                            allowClear
                            style={{ width: '100%' }}
                            onChange={handleCategory}
                            options={songData.categories.map(
                                (category: { id: number; name: string }) => {
                                    return {
                                        value: category.id,
                                        label: category.name,
                                    };
                                }
                            )}
                        />
                    </Col>
                </Row>
                {searchText !== '' && (
                    <Text>
                        当前展示“{searchText}”的搜索结果，共{filterData.length}
                        首
                    </Text>
                )}
            </Space>
            <Table<DataType>
                columns={columns}
                dataSource={filterData}
                pagination={{ position: ['none', 'none'] }}
            />
        </>
    );
};

export default SongManage;
